import { Injectable, signal } from '@angular/core';
import {
  CheckboxFilter,
  ChipsFilter,
  DateRangeFilter,
  DropdownFilter,
  FilterCriteria,
  FilterType,
  InputFilter,
  MultiselectFilter,
  SearchCriteriaCursor,
} from '@shared/components/filters/Domain/filter.model';
import { TranslateService } from '@ngx-translate/core';
import { SearchOrigin } from '@shared/components/filters/Domain/search-method.enum';
import { FilterStorageService } from '@shared/components/filters/Application/Services/filter-storage.service';
import { ActivatedRoute } from '@angular/router';
import { PaymentCatalogStore } from '@shared/services/payment-catalog-store.service';

@Injectable({ providedIn: 'root' })
export class PaymentsFacade {
  loading = signal<boolean>(false);
  lastCriteria = signal<FilterCriteria>({});
  actualFirst = signal<number>(0);
  numResultsDisplayed = signal<number>(10);

  key = 'payments-filters';

  constructor(
    private translate: TranslateService,
    private filterService: FilterStorageService,
    private router: ActivatedRoute,
    private paymentCatalogStore: PaymentCatalogStore,
  ) {}

  getFilters(
    collectors: any[],
  ): (MultiselectFilter | InputFilter | DateRangeFilter | ChipsFilter | CheckboxFilter)[] {
    return [
      {
        id: 'externalIds',
        label: this.translate.instant('DATA.MODULES.PAYMENTS.FILTERS.PAYMENTS_EXTERNAL_IDS'),
        type: FilterType.Chips,
        icon: 'pi pi-credit-card',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.SEARCH_TOOLTIP_TYPE', {
          message: 'Payment IDs',
        }),
      } as ChipsFilter,
      {
        id: 'customerExternalIds',
        label: this.translate.instant('DATA.MODULES.INSTANCES.FORM.LABELS.CUSTOMER_EXTERNAL'),
        type: FilterType.Chips,
        icon: 'pi pi-user',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.SEARCH_TOOLTIP_TYPE', {
          message: 'Customer IDs',
        }),
      } as ChipsFilter,
      {
        id: 'operationExternalIds',
        label: this.translate.instant('DATA.MODULES.PAYMENTS.FILTERS.OPERATION_EXTERNAL_IDS'),
        type: FilterType.Chips,
        icon: 'pi pi-file',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.SEARCH_TOOLTIP_TYPE', {
          message: 'Operation IDs',
        }),
      } as ChipsFilter,
      {
        id: 'methods',
        label: this.translate.instant('DATA.MODULES.PAYMENTS.FILTERS.PAYMENT_METHODS'),
        type: FilterType.Multiselect,
        icon: 'pi pi-wallet',
        options: this.getPaymentMethodOptions(),
      } as MultiselectFilter,
      {
        id: 'paymentGateways',
        label: this.translate.instant('DATA.MODULES.PAYMENTS.FILTERS.PAYMENT_GATEWAY'),
        type: FilterType.Multiselect,
        icon: 'pi pi-ticket',
        options: this.getPaymentGatewayOptions(),
      } as MultiselectFilter,
      {
        id: 'types',
        label: this.translate.instant('DATA.MODULES.PAYMENTS.FILTERS.TYPES'),
        type: FilterType.Checkbox,
        icon: 'pi pi-tags',
        options: [
          {
            label: this.translate.instant('DATA.MODULES.PAYMENTS.FILTERS.PAYMENT'),
            value: 'payment',
          },
          {
            label: this.translate.instant('DATA.MODULES.PAYMENTS.FILTERS.REFUND'),
            value: 'refund',
          },
        ],
      } as CheckboxFilter,
      {
        id: 'collectorIds',
        label: this.translate.instant('DATA.MODULES.PAYMENTS.FILTERS.COLLECTOR'),
        type: FilterType.Multiselect,
        icon: 'pi pi-box',
        options: collectors
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((collector) => {
            return {
              label: collector.name,
              value: collector.id,
            };
          }),
      } as MultiselectFilter,

      {
        id: 'createdAtDateInterval',
        label: this.translate.instant('DATA.MODULES.PAYMENTS.FILTERS.DATE_INTERVAL'),
        type: FilterType.DateRange,
        icon: 'pi pi-calendar',
        minDate: '2020-01-01',
        maxDate: '2030-12-31',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.RANGE_TOOLTIP'),
      } as DateRangeFilter,
    ];
  }

  resolveSearchCriteria(
    event: FilterCriteria | undefined,
    origin: SearchOrigin,
  ): SearchCriteriaCursor {
    const saved = this.filterService.load(this.key);

    let filtersToUse: FilterCriteria;

    if (origin === SearchOrigin.FILTER_CHANGE || origin === SearchOrigin.RESET_OR_RELOAD) {
      filtersToUse = event ?? {};
    } else if (origin === SearchOrigin.LOAD_NEXT) {
      filtersToUse = saved ?? {};
    } else {
      filtersToUse = event ?? saved ?? {};
    }

    const criteria = this.buildPaymentsCallCriteria(filtersToUse, {
      offset: this.actualFirst() || 0,
      limit: this.numResultsDisplayed() || 10,
    });

    this.lastCriteria.set(filtersToUse);

    if (origin === SearchOrigin.FILTER_CHANGE) {
      this.actualFirst.set(0);
      this.filterService.save(this.key, filtersToUse);
    } else if (origin === SearchOrigin.RESET_OR_RELOAD) {
      this.actualFirst.set(0);
      this.filterService.clear(this.key);
      criteria.pagination = {
        cursor: null,
        limit: this.numResultsDisplayed(),
      };
    }

    return criteria;
  }

  private buildPaymentsCallCriteria(
    filters?: FilterCriteria,
    pagination?: { offset: number; limit: number } | undefined,
  ): SearchCriteriaCursor {
    return {
      pagination: {
        cursor: pagination?.offset ? String(pagination.offset) : null,
        limit: pagination?.limit ?? this.numResultsDisplayed(),
      },
      filters: {
        tenantId: this.router.snapshot.queryParamMap.get('tenant'),
        operationExternalIds: filters?.['operationExternalIds'] ?? [],
        externalIds: filters?.['externalIds'] ?? [],
        types: filters?.['types'] ?? [],
        paymentGateways: filters?.['paymentGateways'] ?? [],
        methods: filters?.['methods'] ?? [],
        customerExternalIds: filters?.['customerExternalIds'] ?? [],
        collectorIds: filters?.['collectorIds'] ?? [],
        ...(filters?.['createdAtDateInterval']?.start &&
          filters?.['createdAtDateInterval']?.finish && {
            createdAtDateInterval: {
              start: filters['createdAtDateInterval'].start,
              finish: filters['createdAtDateInterval'].finish,
            },
          }),
      },
    };
  }

  setPageSize(size: number) {
    if (this.numResultsDisplayed() === size) return;
    this.numResultsDisplayed.set(size);
  }

  private getPaymentGatewayOptions(): { label: string; value: string }[] {
    return this.paymentCatalogStore.paymentGatewayTypes().map((gt) => ({
      label: gt.name,
      value: gt.key,
    }));
  }

  private getPaymentMethodOptions(): { label: string; value: string }[] {
    return this.paymentCatalogStore.paymentMethods().map((pm) => ({
      label: pm.name,
      value: pm.key,
    }));
  }
}
